import {
  useActiveFeatureFlags,
  useFeatureFlagVariantKey,
  usePostHog,
} from 'posthog-js/react';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StepConfigItem } from '~/Interfaces';
import { marketHasCheckpointStep } from '~/constants/markets';
import {
  ApplicationInfoLevel,
  ClientAssessmentQuestionnaireTypeId,
  CountryCode,
} from '~/enums';
import { applicationCanHaveGuarantors } from '~/helpers/shared.helper';
import { logToSentry } from '~/helpers/loggers.helper';
import { isRecoverySession } from '~/helpers/recoveryCampaign.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { ILoanApplicationState } from '~/interfaces/loanApplication';
import { RootState } from '~/store';
import { useGetClientAssessmentQuestionsQuery } from '~/store/apis/endpoints/clientAssessmentApi/clientAssessment.api';
import {
  setCurrentStepName,
  setNextStepName,
} from '~/store/slices/loanApplication.slice';
import { isBelgiumClientAssessmentInSync } from '../LegalQuestions/LegalQuestions.utils';
import {
  stepConfigPerMarketFlowType,
  threeStepBasicFlowConfigPerFlowType,
} from '../stepConfigs';

interface UseStepConfigReturn {
  stepConfig: StepConfigItem[];
  currentStepConfig: StepConfigItem | undefined;
  currentStepName: ILoanApplicationState['currentStepName'];
  nextStepName: ILoanApplicationState['nextStepName'];
  isLastStep: boolean;
  isLastBasicStep: boolean;
  turnoverQuestionIsAfterCheckpoint: boolean;
  applicationCanHaveGuarantors: boolean;
}

const useStepConfig = (): UseStepConfigReturn => {
  const dispatch = useAppDispatch();
  const postHog = usePostHog();
  const activeFeatureFlags = useActiveFeatureFlags();

  // Get all required state in one selector for performance
  const {
    loanApplication: {
      clientId,
      flowType,
      currentStepNumber,
      form: {
        clientType,
        bankIntegration,
        bankConnected,
        companyTypeCategory,
        guarantors,
        choseToBoostApplication,
        choseToSubmitBasicApplication,
      },
      apiStatus: { guarantors: guarantorsApiStatus },
      infoLevel,
    },
    intl: { market },
  } = useSelector((state: RootState) => state);

  const cardOptInFeatureFlagVariant = useFeatureFlagVariantKey(
    'AB_Web_LoanApplication_CardOptIn_MoreCardApplications'
  );

  const nlThreeStepFlagVariant = useFeatureFlagVariantKey(
    'AB_Web_LoanApplication_ContactDetailsEarlier_MoreRecoverableLeads_NL'
  );
  // Recovery-email applicants dropped off in the test variant, so they always get it again
  const isRecovery = useMemo(() => isRecoverySession(), []);
  const isPartOfNLThreeStepVariant =
    (isRecovery && market === CountryCode.NL) ||
    nlThreeStepFlagVariant === 'test';

  const {
    data: legalQuestions,
    isSuccess: isLegalQuestionsQuerySuccess,
    isError: isLegalQuestionsQueryError,
  } = useGetClientAssessmentQuestionsQuery(
    { typeId: ClientAssessmentQuestionnaireTypeId.LegalQuestionsBelgium },
    {
      skip: !clientId || market !== CountryCode.BE,
    }
  );

  const isLegalQuestionsOutOfSync =
    isLegalQuestionsQuerySuccess &&
    !isBelgiumClientAssessmentInSync(legalQuestions);

  const shouldHideLegalQuestionsStep =
    isLegalQuestionsQueryError || isLegalQuestionsOutOfSync;
  // Filter steps based on business logic and feature flags
  const stepConfig = useMemo(() => {
    const baseStepConfig = isPartOfNLThreeStepVariant
      ? threeStepBasicFlowConfigPerFlowType[flowType]
      : stepConfigPerMarketFlowType[market]?.[flowType];
    if (!baseStepConfig) {
      return [];
    }

    const shouldHideBasicSteps =
      marketHasCheckpointStep[market] &&
      infoLevel !== ApplicationInfoLevel.PRE_BASIC &&
      choseToSubmitBasicApplication;

    const shouldHideSuperSteps =
      marketHasCheckpointStep[market] && !choseToBoostApplication;

    const isPartOfCardOptInABTestVariant =
      cardOptInFeatureFlagVariant === 'test';

    const filteredStepConfig = baseStepConfig.filter((step: StepConfigItem) => {
      let shouldHideStep = false;

      switch (step.name) {
        case 'company_selector_step':
        case 'start_application_step':
        case 'loan_purpose_step':
        case 'connect_to_eskat_step':
        case 'personal_information_step':
        case 'business_needs_step':
          shouldHideStep = shouldHideBasicSteps;
          break;

        case 'legal_questions_step':
          shouldHideStep = shouldHideBasicSteps || shouldHideLegalQuestionsStep;
          break;

        case 'card_opt_in_step':
          shouldHideStep =
            !isPartOfCardOptInABTestVariant || !shouldHideBasicSteps;
          break;

        case 'checkpoint_step':
          shouldHideStep = !shouldHideBasicSteps;
          break;
        case 'application_overview_step':
          shouldHideStep = !shouldHideBasicSteps || choseToBoostApplication;
          break;

        case 'guarantor_selector_step':
          shouldHideStep =
            !applicationCanHaveGuarantors(
              market,
              companyTypeCategory,
              guarantors,
              guarantorsApiStatus
            ) ||
            (marketHasCheckpointStep[market] && !choseToBoostApplication);
          break;

        case 'connect_to_bank_through_klarna_kosma_step': {
          const marketHasEnableBankingFeature = Boolean(
            postHog.isFeatureEnabled('Onboarding_MarketHasEnableBankingFeature')
          );
          const isKlarnaKosmaDead = Boolean(
            postHog.isFeatureEnabled('Onboarding_IsKlarnaKosmaDead')
          );
          const successfullyConnectedToBankThroughKlarnaKosma = Boolean(
            bankIntegration
          );
          shouldHideStep =
            isKlarnaKosmaDead ||
            marketHasEnableBankingFeature ||
            successfullyConnectedToBankThroughKlarnaKosma ||
            shouldHideSuperSteps;
          break;
        }

        case 'upload_documents_step':
          shouldHideStep = Boolean(bankConnected) || shouldHideSuperSteps;
          break;

        case 'financial_information_step':
          shouldHideStep = shouldHideSuperSteps;
          break;

        case 'connect_to_bank_step': {
          const marketHasEnableBankingFeature = Boolean(
            postHog.isFeatureEnabled('Onboarding_MarketHasEnableBankingFeature')
          );
          shouldHideStep =
            !marketHasEnableBankingFeature || shouldHideSuperSteps;
          break;
        }

        default:
          break;
      }

      return !shouldHideStep;
    });

    return filteredStepConfig;
  }, [
    market,
    flowType,
    clientType,
    bankIntegration,
    bankConnected,
    companyTypeCategory,
    guarantors,
    guarantorsApiStatus,
    choseToBoostApplication,
    choseToSubmitBasicApplication,
    activeFeatureFlags,
    infoLevel,
    cardOptInFeatureFlagVariant,
    isPartOfNLThreeStepVariant,
    shouldHideLegalQuestionsStep,
  ]);

  // Calculate derived values
  const derivedValues = useMemo(() => {
    const currentStepIndex = currentStepNumber - 1; // currentStep is 1-indexed, stepConfig is 0-indexed
    const currentStepConfig = stepConfig[currentStepIndex];
    const nextStepConfig = stepConfig[currentStepIndex + 1] as  // can be removed when "noUncheckedIndexedAccess": true is added to tsconfig
      | StepConfigItem
      | undefined;
    const currentStepName = currentStepConfig?.name;
    const nextStepName = nextStepConfig?.name || null;
    const isLastStep =
      stepConfig.length === currentStepNumber &&
      (choseToSubmitBasicApplication || !marketHasCheckpointStep[market]);
    const isLastBasicStep =
      stepConfig.length === currentStepNumber &&
      !choseToSubmitBasicApplication &&
      marketHasCheckpointStep[market];

    // NOTE: intentionally reads the unfiltered/control stepConfigPerMarketFlowType
    // rather than the NL-three-step-variant-aware array above. Harmless today:
    // NL never has a 'financial_information_step' in either variant, so
    // indexOfTurnoverStep is -1 regardless.
    const indexOfTurnoverStep = stepConfigPerMarketFlowType[market]?.[
      flowType
    ]?.findIndex((step) => step.name === 'financial_information_step');
    const indexOfCheckpointStep = stepConfigPerMarketFlowType[market]?.[
      flowType
    ]?.findIndex((step) => step.name === 'checkpoint_step');

    const turnoverQuestionIsAfterCheckpoint =
      indexOfTurnoverStep > indexOfCheckpointStep;

    const _applicationCanHaveGuarantors = applicationCanHaveGuarantors(
      market,
      companyTypeCategory,
      guarantors,
      guarantorsApiStatus
    );

    return {
      currentStepConfig,
      currentStepName,
      nextStepName,
      isLastStep,
      isLastBasicStep,
      turnoverQuestionIsAfterCheckpoint,
      applicationCanHaveGuarantors: _applicationCanHaveGuarantors,
    };
  }, [
    stepConfig,
    currentStepNumber,
    infoLevel,
    choseToSubmitBasicApplication,
    marketHasCheckpointStep[market],
  ]);

  // Update Redux store with current step names for tracking and business logic
  useEffect(() => {
    dispatch(setCurrentStepName(derivedValues.currentStepName));
    dispatch(setNextStepName(derivedValues.nextStepName));
  }, [dispatch, derivedValues.currentStepName, derivedValues.nextStepName]);

  useEffect(() => {
    if (isLegalQuestionsQueryError) {
      logToSentry(
        new Error(
          `Failed to load Belgium client assessment questionnaire for typeId: ${ClientAssessmentQuestionnaireTypeId.LegalQuestionsBelgium}`
        ),
        'useStepConfig/belgiumClientAssessmentQuestionnaireFetchError'
      );
    } else if (
      isLegalQuestionsQuerySuccess &&
      !isBelgiumClientAssessmentInSync(legalQuestions)
    ) {
      logToSentry(
        new Error(
          `Belgium client assessment questionnaire is out of sync with the API response for typeId: ${ClientAssessmentQuestionnaireTypeId.LegalQuestionsBelgium}`
        ),
        'useStepConfig/belgiumClientAssessmentQuestionnaireOutOfSync'
      );
    }
  }, [
    isLegalQuestionsQueryError,
    isLegalQuestionsQuerySuccess,
    legalQuestions,
  ]);

  return {
    stepConfig,
    ...derivedValues,
  };
};

export default useStepConfig;
